import { useState, type ReactNode } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";

export interface NotAResellerSendMailInput {
  to: string[];
  subject: string;
  body: string;
  templateName: string;
  isBodyHtml: boolean;
  from?: string;
  replyTo?: string[];
}

export interface NotAResellerProps {
  /** Tenant id shown in the activation email metadata. */
  currentTenantId?: string;
  /** Recipient for the activation request email. */
  activationRecipient?: string;
  /** Sends the activation request. Returns whether the API reported success. */
  onSendMail: (input: NotAResellerSendMailInput) => Promise<{
    isSuccess: boolean;
    message?: string | null;
  }>;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  /** Optional extra content below the form (e.g. app-specific links). */
  children?: ReactNode;
}

const DEFAULT_ACTIVATION_RECIPIENT = "info@orda-app.com";

/**
 * Shown when the signed-in tenant is not an activated reseller.
 * Apps supply their own SendMail mutation via `onSendMail`.
 */
export function NotAReseller({
  currentTenantId,
  activationRecipient = DEFAULT_ACTIVATION_RECIPIENT,
  onSendMail,
  onSuccess,
  onError,
  children,
}: NotAResellerProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const body = [
        message.trim(),
        "",
        "---",
        `${t("notAReseller.email", { defaultValue: "Email" })}: ${user?.email ?? "–"}`,
        `${t("notAReseller.tenantId", { defaultValue: "Tenant ID" })}: ${currentTenantId ?? "–"}`,
      ].join("\n");

      const res = await onSendMail({
        to: [activationRecipient],
        subject: t("notAReseller.emailSubject", {
          defaultValue: "Reseller activation request",
        }),
        body,
        templateName: "base_template.html",
        isBodyHtml: true,
        ...(user?.email ? { from: user.email, replyTo: [user.email] } : {}),
      });

      if (!res.isSuccess) {
        onError?.(
          res.message ??
            t("notAReseller.sendError", { defaultValue: "Could not send." }),
        );
        return;
      }

      setSent(true);
      onSuccess?.(
        t("notAReseller.sendSuccess", { defaultValue: "Request sent." }),
      );
    } catch (e) {
      onError?.(
        e instanceof Error
          ? e.message
          : t("notAReseller.sendError", { defaultValue: "Could not send." }),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            {t("notAReseller.title", {
              defaultValue: "Not a reseller account",
            })}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("notAReseller.description", {
              defaultValue:
                "Your account is not activated as a reseller. Send us a message to request activation.",
            })}
          </Typography>

          {sent ? (
            <Typography color="success.main" sx={{ mb: 3 }}>
              {t("notAReseller.sendSuccess", { defaultValue: "Request sent." })}
            </Typography>
          ) : (
            <>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label={t("notAReseller.messageLabel", {
                  defaultValue: "Message",
                })}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={sending || !message.trim()}
                onClick={() => void handleSend()}
                sx={{ mb: 2 }}
              >
                {sending
                  ? t("Loading...", { defaultValue: "Loading…" })
                  : t("notAReseller.send", { defaultValue: "Send request" })}
              </Button>
            </>
          )}

          {children}

          <Button fullWidth variant="text" onClick={logout}>
            {t("notAReseller.logout", { defaultValue: "Log out" })}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
